/**
 * The Price Book shelf (T387 M5, rates-386 Direction 1): covers + one search,
 * nothing else. Covers say where every price lives; the chips are COMPUTED from
 * the rates rows, never decorative ("2 to review" = the number of provisional
 * location-rows in that list).
 *
 * ⛔ EVERY COVER KEEPS ITS `aria-label` AND ADDS `aria-describedby` (T937). Do
 * NOT "tidy" either half away, and do NOT drop the label in favour of the
 * subtree — the two attributes are doing two different jobs:
 *   - `aria-label` REPLACES the name computed from the subtree, so on its own it
 *     made the computed payload described above -- the status chip -- inaudible:
 *     a screen-reader user heard "Airport transfers" and never "All prices set".
 *   - It cannot simply be removed, because the net addresses covers BY NAME
 *     (e2e/tests/green/rates-book-powerscourt.spec.ts:45, :111 and
 *     test/client/admin-screens.spec.tsx:1668-1669 all assert the exact label).
 * `aria-describedby` contributes to the DESCRIPTION and never to the name, so
 * the chip is announced after a name that stays byte-identical. Ids are
 * generated per service because SERVICE_ORDER renders N covers; a constant id
 * would collide.
 *
 * ⛔ T1749 M9 (DS-24) — THE META LINE IS GONE AND THE DESCRIPTION NARROWED WITH
 * IT. `.cover-meta` carried a by-hand count sentence ("28 places · 4 seat
 * bands", "Priced by hand today", "N priced add-ons · 3 rules"): numbers written
 * in prose, with nothing asserting them, on the one screen whose chips are
 * computed. Each `aria-describedby` now names its STATUS id ALONE — ⛔ a
 * describedby naming a removed id is a dangling reference, which is silent, so
 * the two halves move together or neither does.
 *
 * THE COUNT, corrected in T928 — it had been one cover ahead of the code since
 * T407 retired `private-tour`, and the stale figure was copied to ten sites
 * where it read as the governing budget:
 *
 *     4 service covers (SERVICE_ORDER) + Add-ons          = 5 fixed
 *     + "Needs a decision" whenever the queue read SUCCEEDED
 *                                                          = 6 normally
 *     + one search                                          = 7 interactive
 *
 * So "six covers plus a search" is now ACCURATE for the normal case rather than
 * aspirational — but note it got there by board 926 making the queue cover
 * unconditional-on-success, not by anyone counting again. During a queue outage
 * the shelf is 5 covers.
 *
 * ⚠️ Nothing asserts this count. All ten sites carrying the old figure were
 * prose, so a cover added or removed here will contradict the docstrings
 * silently — which is exactly how it went stale the first time.
 *
 * ⭐ BOARD 938 CLOSED THAT GAP, and in doing so showed the count above is
 * measuring the wrong thing to certify the rule by.
 *   - The arithmetic is now ASSERTED, in test/client/control-budget.spec.tsx.
 *     The warning immediately above is answered; leave it as the record of why
 *     the pin exists.
 *   - ⛔ BUT "= 7 interactive" IS A COMPONENT COUNT, AND DESIGN.md §1 RULE 2 IS
 *     A SCREEN RULE ("≤ 7 interactive elements in the content area"). The shelf
 *     SCREEN is NINE: everything below, plus App.tsx's Back button and Admin's
 *     "Customer accounts & prices" quiet entry, all of which render into the
 *     same <main class="content">. So Shelf.tsx is exactly at budget while the
 *     screen it lives on is two over.
 *   - That breach is RECORDED, not fixed — Alex ruled the screen is not to be
 *     redesigned for it (T938 R2 (a)). Adding a cover here still costs the
 *     screen a control it does not have; the pin will say so.
 */
import { cardClass } from '@ecmb/ui';
import { useState } from 'preact/hooks';
import { PRICE_ENTRY_KIND_BY_SERVICE, type ServiceId } from '../../../shared/service-schema.js';
import type { ApiRateRow } from '../../lib/actions.js';
import type { Registry } from '../../store/registry.js';

/** Book-specific cover names (design spec copy — NOT the service labels). */
export const COVER_NAMES: Readonly<Record<string, string>> = {
  airport: 'Airport transfers',
  golf: 'Golf routes',
  wedding: 'Weddings',
  'local-other': 'Custom journeys',
  'private-tour': 'Private tours',
};

/** Row-noun per list ("28 places", "21 routes", "6 venues"). */
export const LIST_NOUNS: Readonly<Record<string, [string, string]>> = {
  airport: ['place', 'places'],
  golf: ['route', 'routes'],
  wedding: ['venue', 'venues'],
  'private-tour': ['destination', 'destinations'],
  'local-other': ['journey', 'journeys'],
};

// T407 (D3): private-tour is retired — no shelf cover (0 rate rows existed;
// historical BOOKINGS still render via the full registry, this is price admin).
const SERVICE_ORDER = ['airport', 'golf', 'wedding', 'local-other'] as const;

export type ShelfProps = {
  registry: Registry;
  rates: ApiRateRow[];
  /** Total waiting cards (M6). The "Needs a decision" cover sorts FIRST (Alex
   *  ruling, L5) and, since board 926, renders at ZERO too — see `queueFailed`.
   *  Board 938: this count now also drives the cover's GOLD TINT. Zero renders
   *  the cover untinted, so gold means "there is a decision to make" on the tile
   *  and on its chip alike, rather than the tile saying gold while the chip
   *  said green. Sorting first is unchanged and does not depend on the count. */
  queueCount: number;
  /**
   * ⭐ Board 926 (T928) — TRUE only when the queue READ FAILED.
   *
   * The cover used to render only when `queueCount > 0`, and it is the ONLY
   * entry point to the promote queue, so an empty queue made that surface
   * unreachable from the UI entirely. It now renders at zero as well.
   *
   * ⛔ WHICH IS EXACTLY WHY THIS FLAG HAS TO EXIST. Admin's queue read used to
   * catch to an empty queue, so "nothing is waiting" and "I could not find out"
   * arrived here as the same value — and once the cover renders at zero, a dead
   * read would say "Nothing waiting" about a queue that might be full. That is a
   * false reassurance on the one surface whose whole job is telling Helen there
   * is a decision outstanding. On a failed read the cover is withheld instead,
   * which is the behaviour that shipped before and is pinned by its own spec
   * ("a queue read failure degrades to the plain shelf — no cover, no false
   * error").
   */
  queueFailed: boolean;
  onOpen: (service: string, filter?: string) => void;
  onOpenAddons: () => void;
  onOpenQueue: () => void;
};

/** Distinct location count for a service. */
function locationCount(rates: ApiRateRow[], service: string): number {
  return new Set(rates.filter((r) => r.service === service).map((r) => r.locationId)).size;
}

/** Distinct provisional location-rows (operator-added, not yet ratified). */
function provisionalCount(rates: ApiRateRow[], service: string): number {
  return new Set(
    rates.filter((r) => r.service === service && r.operatorAdded && !r.ratified).map((r) => r.locationId),
  ).size;
}

export function Shelf({ registry, rates, queueCount, queueFailed, onOpen, onOpenAddons, onOpenQueue }: ShelfProps) {
  const [q, setQ] = useState('');
  const meta = (registry.options.meta ?? {}) as Record<string, string>;
  /* ⭐ T1749 M9 (DS-24) — THE COVER'S BOX IS THE LIBRARY'S `Card`, composed onto
     the <button> a cover has to be (`Card` itself renders a <div>). app.css's
     `.book-shelf .card` carries only what a button needs on top of it.
     ⛔ BOUND HERE RATHER THAN CALLED INSIDE THE JSX, and that is a measured
     constraint rather than tidiness. `style-contract.spec.ts`'s
     `appliedClasses()` takes every string literal inside a `class={…}` region,
     so calling the composer in place reports its SHAPE ARGUMENT (`cover`) as a
     class the markup applies with no rule behind it — a false red, the sibling
     of that scan's own "a string being COMPARED is not a class being applied"
     repair. Bound once, the region holds no literal but the real modifier. */
  const coverClass = cardClass('cover');
  const ruleToConfirm = meta['return_rule_status'] !== 'confirmed';

  const coverChip = (service: string) => {
    const provisional = provisionalCount(rates, service);
    if (provisional > 0) {
      return <span class="chip chip--gold">{provisional} to review</span>;
    }
    if (locationCount(rates, service) > 0) {
      return <span class="chip chip--ok">All prices set</span>;
    }
    /* ⛔⛔ T1749 M9 rework 2 (Captain's four-gate check) — ONE WORD, NOT TWO, AND
       WHAT THE COLLAPSE TRADES IS WRITTEN DOWN HERE RATHER THAN LOST.

       The two arms below said the same thing two ways — `No set prices` and `No
       price list yet` — on covers a reader crosses in one glance, and Captain
       ruled them one. `No set prices` is the survivor because it is TRUE in both
       cases and promises nothing, where `No price list yet` implies a list is
       coming, which is false for a hand-priced service.

       ⛔ AND THEY ARE NOT OBVIOUSLY THE SAME FACT, which is the cost of the
       ruling, stated so the next reader (and Captain) can see what was traded:
         · `PRICE_ENTRY_KIND_BY_SERVICE[service] == null` — the service is priced
           BY HAND, by judgment, and will NEVER have a list. Nothing is missing.
         · otherwise — the service takes a price list and one has not been made
           yet. Something IS missing, and somebody has to go and make it.
       One chip now covers both, so a cover no longer distinguishes "there is
       nothing to do here" from "there is work waiting". What still does: the
       `2 to review` / `All prices set` arms above fire only on a service that HAS
       rows, and opening the list shows an entry form on one and not the other.
       ⚠️ WHAT WOULD LIFT IT: a second chip TONE for the two meanings (not a second
       sentence), reviewed at a sitting — the collision that produced this ruling
       was two sentences, not two states. */
    return <span class="chip chip--quiet">No set prices</span>;
  };

  // Global search: finds any place, venue or route across all lists and jumps
  // straight into the right one (with the list pre-filtered to the hit).
  const needle = q.trim().toLowerCase();
  const hits: { service: string; label: string }[] = [];
  if (needle.length >= 2) {
    const seen = new Set<string>();
    for (const r of rates) {
      const key = `${r.service}|${r.locationId}`;
      if (seen.has(key) || !r.label.toLowerCase().includes(needle)) continue;
      seen.add(key);
      hits.push({ service: r.service, label: r.label });
      if (hits.length >= 6) break;
    }
  }

  return (
    <>
      {/* ⛔ THE EXPLANATORY LINE IS GONE (T1749 M10 rework 1, Captain's live pass
          item 6). It read "The price book. Open a list to see or change prices." and
          broke the rule twice over. As COPY it explained what the screen already
          shows: the rail row that reaches here says `Price lists`, and a shelf of
          covers is self-evidently openable — Q139's rule is that a hint earns its
          place by saying what is not obvious, and nothing here was unobvious. As
          MARKUP it was the only `<p>` in either tree wearing `.screen-sub`, a HEADING
          class (`--text-emphasis`, weight 600, navy): every other user is an `<h2>`
          or `<h3>`, so a paragraph was being painted as a section title. Deleting it
          settles both. Derive the misuse:
          `/usr/bin/grep -rn 'class="screen-sub"' quote-helper/src/client`. */}
      <div class="book-search">
        <input
          class="login-input"
          type="text"
          placeholder="Find a place, venue or route"
          aria-label="Find a place, venue or route"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        />
      </div>
      {hits.length > 0 && (
        <div class="shelf-hits">
          {hits.map((h) => (
            <button
              key={`${h.service}|${h.label}`}
              type="button"
              class="shelf-hit"
              onClick={() => onOpen(h.service, h.label)}
            >
              {h.label}
              <span class="hit-list">{COVER_NAMES[h.service] ?? h.service}</span>
            </button>
          ))}
        </div>
      )}
      <div class="book-shelf">
        {!queueFailed && (
          <button
            type="button"
            /* ⭐ THE GOLD TINT IS CONDITIONAL ON THERE BEING WORK (board 938, Alex ruling).
               It used to be unconditional, and board 926 then made the cover render at zero
               as well — so in the steady state a gold "decide me" tile announced its own
               irrelevance, reading "Nothing waiting" while tinted for attention. The chip
               ONE LINE BELOW already encoded gold-means-work correctly (`chip--gold` when
               `queueCount > 0`, `chip--ok` otherwise), so the tile contradicted its own chip.
               ⛔ KEYS ON `queueCount`, NEVER ON `queueFailed`. 926's reachability rule is
               untouched above: a SUCCESSFUL read still renders the cover at zero (that is the
               only door to the queue), and a FAILED read still renders no cover at all. This
               changes the tint only.
               ⛔ TERNARY AND CONCATENATION, NEVER A TEMPLATE LITERAL: style-contract.spec.ts
               replaces `${...}` with an @INTERP@ sentinel and drops every token touching it,
               so the template form would hide the modifier from that scanner.
               ⚠️ T1749 M9 (DS-24) renamed the modifier `book-cover--queue` -> `cover-queue`
               with the base box: a `--` modifier on a base the markup no longer wears is a
               dangling limb, and a STANDALONE `.book-cover--queue` rule declaring a
               background re-founds the census family the adoption retires (families.py
               collapses a modifier onto its base). `admin-screens.spec.tsx` asserts the base
               class AND the modifier together, which is the property that survives. */
            class={queueCount > 0 ? coverClass + ' cover-queue' : coverClass}
            aria-label="Needs a decision"
            aria-describedby="cover-queue-status"
            onClick={onOpenQueue}
          >
            <span class="cover-name">Needs a decision</span>
            <span class="cover-status" id="cover-queue-status">
              {queueCount > 0
                ? <span class="chip chip--gold">{queueCount} waiting</span>
                : <span class="chip chip--ok">Nothing waiting</span>}
            </span>
          </button>
        )}
        {SERVICE_ORDER.map((service) => (
          <button
            key={service}
            type="button"
            class={coverClass}
            aria-label={COVER_NAMES[service]}
            aria-describedby={`cover-${service}-status`}
            onClick={() => onOpen(service)}
          >
            <span class="cover-name">{COVER_NAMES[service]}</span>
            <span class="cover-status" id={`cover-${service}-status`}>{coverChip(service)}</span>
          </button>
        ))}
        <button
          type="button"
          class={coverClass}
          aria-label="Add-ons & rules"
          aria-describedby="cover-addons-status"
          onClick={onOpenAddons}
        >
          <span class="cover-name">Add-ons &amp; rules</span>
          <span class="cover-status" id="cover-addons-status">
            {ruleToConfirm
              ? <span class="chip chip--goldline">1 rule to confirm</span>
              : <span class="chip chip--ok">All prices set</span>}
          </span>
        </button>
      </div>
    </>
  );
}
